import i18n from '@/i18n';
import { notificationService } from '@/services/notification/notification-service';
import { CopyMonitorError } from '@/services/sharepoint-drive/copy-monitor-error';
import { GraphRequestError } from '@/services/graph/graph-request-error';
import type { ItemReference } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { onBeforeUnmount, inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import { useCopyItemMutation } from '../../data/useCopyItemMutation';
import { useMoveItemMutation } from '../../data/useMoveItemMutation';
import type { FileSystemItem } from '../../types';
import {
  mountDisplayName,
  type FileManagerMount,
  type FileManagerMountsState,
} from '../core/useFileManagerMounts';
import type { FileManagerNavigationState } from '../core/useFileManagerNavigation';
import { runWithConcurrency } from '../upload/concurrencyPool';
import type { TransferPolicy } from './transferPolicy';

// A copy is one POST plus a monitor poll that lives for the whole copy, so an unbounded fan-out
// would leave a large selection holding that many concurrent polls for minutes — exactly the
// throttling hazard concurrencyPool was written for. Same value as DEFAULT_UPLOAD_CONCURRENCY.
// Moves ride the same pool: with short single requests, 4 is indistinguishable from unbounded for
// any realistic selection, which is what lets one runTransfer honestly serve both verbs.
const TRANSFER_CONCURRENCY = 4;

/**
 * A drop that has been evaluated but not yet performed — everything runTransfer needs, plus what the
 * confirmation modal renders itself from. Held as ONE nullable ref: non-null *is* "the modal is
 * open", so there is no second boolean that can fall out of step with it.
 */
export type PendingTransfer = {
  items: FileSystemItem[];
  destinationParentPath: string;
  /** Materialized drop target, when the zone had one — lets the mutation skip a getItemReference(). */
  destinationItem?: FileSystemItem;
  destinationMount: FileManagerMount;
  policy: TransferPolicy;
};

/**
 * Per-item outcome of a transfer.
 *
 * 'abandoned' is neither success nor failure and is deliberately not collapsed into either: the copy
 * is still running inside SharePoint. 'unmounted' means we stopped watching because the pane went
 * away (silent — nobody is there to read a toast, and refetch-on-mount shows the truth on return);
 * 'timeout' means the deadline expired while the user was still watching, which earns an
 * informational message but must never claim the copy failed.
 */
export type TransferResult =
  | { item: FileSystemItem; status: 'ok' }
  | { item: FileSystemItem; status: 'failed'; error: unknown }
  | { item: FileSystemItem; status: 'abandoned'; reason: 'unmounted' | 'timeout' };

const isNameConflict = (error: unknown): boolean =>
  (error instanceof GraphRequestError && error.status === 409) ||
  (error instanceof CopyMonitorError && error.isNameConflict);

/**
 * The failure wording per verb, spelled out rather than composed as
 * `...${operation}FailedSingle`. Two reasons, both maintenance rather than correctness: a Record over
 * the verb union makes a third operation a compile error instead of a message that silently renders
 * its own key path, and a literal key is one a `grep copyFailedSingle` across the repo can actually
 * find — which is how anyone edits or prunes a locale file.
 */
const FAILURE_KEYS: Record<
  TransferPolicy['operation'],
  { single: string; multiple: string }
> = {
  move: {
    single: 'fileManager.dragDrop.useFileManagerDrag.moveFailedSingle',
    multiple: 'fileManager.dragDrop.useFileManagerDrag.moveFailedMultiple',
  },
  copy: {
    single: 'fileManager.dragDrop.useFileManagerDrag.copyFailedSingle',
    multiple: 'fileManager.dragDrop.useFileManagerDrag.copyFailedMultiple',
  },
};

/**
 * Pure, exported for tests: turns a settled transfer batch into a single aggregate message, or null
 * when nothing failed. Same shape and spirit as summarizeDeleteFailures — silent on success (the
 * batch's own success reporting is separate), the single-failure case names the item, and beyond one
 * failure a count replaces what would become an unreadable list of names.
 *
 * 'abandoned' results are excluded outright: an abandoned poll is not a failed transfer.
 */
export const summarizeTransferFailures = (
  results: TransferResult[],
  operation: TransferPolicy['operation'],
): string | null => {
  const { t } = i18n.global;
  const failed = results.filter((result) => result.status === 'failed');
  if (failed.length === 0) return null;

  if (failed.length === 1) {
    const [only] = failed;
    if (isNameConflict(only.error)) {
      return t('fileManager.dragDrop.useFileManagerDrag.alreadyExists', {
        name: only.item.name,
      });
    }
    return t(FAILURE_KEYS[operation].single, { name: only.item.name });
  }

  return t(FAILURE_KEYS[operation].multiple, {
    failedCount: failed.length,
    totalCount: results.length,
  });
};

export type FileManagerTransferState = {
  /** Paths with a transfer in flight — drives the dimmed row state, for either verb. */
  transferringPaths: Ref<Set<string>>;
  /** Non-null while a disclosure confirmation is awaiting the user. */
  pendingTransfer: Ref<PendingTransfer | null>;
  /** Runs the transfer, or opens the confirmation first when the policy calls for one. */
  requestTransfer: (intent: PendingTransfer) => void;
  confirmTransfer: () => void;
  cancelTransfer: () => void;
};

export const fileManagerTransferKey: InjectionKey<FileManagerTransferState> =
  Symbol('fileManagerTransfer');

export const provideFileManagerTransfer = (
  navigation: FileManagerNavigationState,
  mounts: Pick<FileManagerMountsState, 'resolve'>,
) => {
  const { t } = i18n.global;
  const transferringPaths = ref<Set<string>>(new Set());
  const pendingTransfer = ref<PendingTransfer | null>(null);

  const moveMutation = useMoveItemMutation(navigation, mounts);
  const copyMutation = useCopyItemMutation(mounts);

  // Abandons in-flight copy MONITORS when the pane goes away — never the copies themselves, which
  // SharePoint completes server-side regardless. Without it a poll would outlive the component and
  // keep invalidating queries against a cache the user has navigated away from.
  const abortController = new AbortController();
  onBeforeUnmount(() => abortController.abort());

  const stopTransferring = (path: string) => {
    const next = new Set(transferringPaths.value);
    next.delete(path);
    transferringPaths.value = next;
  };

  const classifyFailure = (item: FileSystemItem, error: unknown): TransferResult => {
    if (error instanceof CopyMonitorError && error.reason !== 'failed') {
      return { item, status: 'abandoned', reason: error.reason === 'timeout' ? 'timeout' : 'unmounted' };
    }
    return { item, status: 'failed', error };
  };

  const runTransfer = async (intent: PendingTransfer) => {
    const { items, policy, destinationParentPath, destinationItem, destinationMount } = intent;
    if (items.length === 0) return;

    const destinationParent: ItemReference | undefined =
      destinationItem?.id && destinationItem?.driveId
        ? { driveId: destinationItem.driveId, id: destinationItem.id }
        : undefined;

    // Marked for EVERY item up front, including the ones the pool has not started yet: a queued item
    // is genuinely pending from the user's side, and leaving it undimmed until a slot frees would
    // read as "that one did not take".
    //
    // Added to what is already in flight, never replacing it: a cross-mount copy runs for minutes, so
    // a second drop landing while the first batch is still going is ordinary rather than exotic (a
    // move batch settled far too fast for it to matter). Replacing the set would un-dim the first
    // batch's rows while they are still copying, and its later per-item cleanup would then delete
    // this batch's entries. stopTransferring only ever removes its own path, so batches interleave
    // safely once the set accumulates.
    transferringPaths.value = new Set([
      ...transferringPaths.value,
      ...items.map((item) => item.path),
    ]);

    const results: TransferResult[] = [];
    // Each item is caught inside the worker, so the pool never rejects and every item still gets its
    // turn — the contract runWithConcurrency documents for exactly this caller shape.
    await runWithConcurrency(items, TRANSFER_CONCURRENCY, async (item) => {
      try {
        if (policy.operation === 'copy') {
          await copyMutation.mutateAsync({
            item,
            destinationParentPath,
            destinationParent,
            signal: abortController.signal,
          });
        } else {
          await moveMutation.mutateAsync({ item, destinationParentPath, destinationParent });
        }
        results.push({ item, status: 'ok' });
      } catch (error) {
        results.push(classifyFailure(item, error));
      } finally {
        // Cleared as each item settles, independently of the slowest in the batch.
        stopTransferring(item.path);
      }
    });

    // Nobody is on screen to read any of this — see 'unmounted' above.
    if (abortController.signal.aborted) return;

    const failureMessage = summarizeTransferFailures(results, policy.operation);
    if (failureMessage) {
      notificationService.open({ type: 'danger', text: failureMessage });
    }

    if (results.some((result) => result.status === 'abandoned' && result.reason === 'timeout')) {
      // Not 'danger': we did not observe a failure, we stopped observing. Saying more than that
      // would be a claim we never verified.
      notificationService.open({
        type: 'info',
        text: t('fileManager.dragDrop.useFileManagerDrag.copyStillRunning'),
      });
    }

    // Only copy reports its own success. A move is instant and self-evident (the row leaves the
    // folder); a copy can run for minutes after a dialog the user was warned by, so finishing
    // silently would leave them with nothing to tell them it landed.
    const copied = results.filter((result) => result.status === 'ok').length;
    if (policy.operation === 'copy' && copied > 0) {
      notificationService.open({
        type: 'success',
        text: t('fileManager.dragDrop.useFileManagerDrag.copySucceeded', {
          count: copied,
          destination: mountDisplayName(destinationMount),
        }),
      });
    }
  };

  const requestTransfer = (intent: PendingTransfer) => {
    if (intent.items.length === 0) return;
    if (intent.policy.requiresConfirmation) {
      pendingTransfer.value = intent;
      return;
    }
    void runTransfer(intent);
  };

  const confirmTransfer = () => {
    const intent = pendingTransfer.value;
    pendingTransfer.value = null;
    if (intent) void runTransfer(intent);
  };

  const cancelTransfer = () => {
    pendingTransfer.value = null;
  };

  const state: FileManagerTransferState = {
    transferringPaths,
    pendingTransfer,
    requestTransfer,
    confirmTransfer,
    cancelTransfer,
  };
  provide(fileManagerTransferKey, state);
  return state;
};

export const useFileManagerTransfer = () => {
  const state = inject(fileManagerTransferKey);
  if (!state) {
    throw new Error(
      'useFileManagerTransfer() must be called within a component tree provided by provideFileManagerTransfer()',
    );
  }
  return state;
};
